import {lazyRegister} from '@/Core/lazyRegister';
import {BaseApiFactory} from "@/Factories/BaseApiFactory";
import {
    ApiResult,
    GetProductInfoByGroup, Pictogram, ProductByDataListFilter,
    ProductDetail, ProductHierarchy, ProductHierarchyItem,
    ProductInDataListItem,
    ProductInfoByCategory,
    ProductSummaryFilter,
    ProductSummaryItem, SalesOrganization, SiteCoreRange
} from "@/models";
import {OptionModel} from "@components/Forms";
import {SearchSkuPromotion} from "@/models/SearchSkuPromotion";

export class ProductService {
    static $inject = ['BaseApiFactory'];

    constructor(private baseApiFactory: BaseApiFactory) {
    }

    GetAllSKU(countryId, cancelToken: AbortSignal = null) {
        return this.baseApiFactory.getData<string[]>('/api/Product/SelectSKU', {countryId}, cancelToken);
    };

    CheckSKUForRuleItem(query, cancelToken: AbortSignal = null) {
        return this.baseApiFactory.getData<SearchSkuPromotion>('/api/Product/CheckSKUForRuleItem', query, cancelToken);
    };

    listProductManage(query: ProductSummaryFilter, cancelToken: AbortSignal = null) {
        return this.baseApiFactory.getData<ProductSummaryItem[]>('/v2/api/product/list-manage', query, cancelToken);
    };

    listProductByDataListByFilter(query: ProductByDataListFilter, cancelToken: AbortSignal = null) {
        return this.baseApiFactory.getData<ProductInDataListItem[]>('/v2/api/product/data-list/filter', query, cancelToken);
    };

    listProductsByDataList(id: number, query: {
        langCode: string,
        countryId: number,
        salesOrganizationId: number
    }, cancelToken: AbortSignal = null) {
        return this.baseApiFactory.getData<ProductInDataListItem[]>('/v2/api/product/data-list/' + id, query, cancelToken);
    };

    /**
     * @deprecated
     * */
    GetProductMaster(query: {
        [key: string]: any
    }, cancelToken: AbortSignal = null) {
        return this.baseApiFactory.getData<any[]>('/api/Product/master-product', query, cancelToken);
    };

    SelectProductDetail(sku: string, langCode, countryId, salesOrgId,
                        cancelToken: AbortSignal = null) {
        return this.baseApiFactory.getData<ProductDetail>(`/api/Product/SelectProductDetail/${sku}/${salesOrgId}`,
            {langCode, countryId}, cancelToken);
    };

    GetAllSalesOrganizations(cancelToken: AbortSignal = null) {
        return this.baseApiFactory.getData<SalesOrganization[]>('/api/SalesOrganization', cancelToken);
    };

    GetAllSeries(query, cancelToken: AbortSignal = null) {
        return this.baseApiFactory.getData<SiteCoreRange[]>(`/api/ProductSeri/${query.countryId}`, null, cancelToken);
    };

    GetAllTypesTree(query: {
        langCode: string,
        countryId: number
    }, cancelToken: AbortSignal = null) {
        return this.baseApiFactory.getData<ProductHierarchy[]>(`/api/ProductType/${query.countryId}/tree`, query, cancelToken);
    };

    async GetAllTypes(query: {
        langCode: string,
        countryId: number
    }, cancelToken: AbortSignal = null): Promise<ApiResult<OptionModel[]>> {
        const res = await this.baseApiFactory.getData<ProductHierarchyItem[]>(`/api/ProductType/${query.countryId}`, query, cancelToken);
        if (!res.IsSuccess) {
            return {
                IsSuccess: false,
                Value: []
            };
        }

        const types = res.Value;
        types.sort((a, b) => a.LangText > b.LangText ? 1 : -1);

        const options = types
            .filter(x => x.Number.length === 5)
            .map((item) => {
                return this.getTypeOptionGroupsRecursive(types, item);
            });

        return {
            Value: options,
            IsSuccess: true
        }
    };

    private getTypeOptionGroupsRecursive(types: ProductHierarchy[], parent: ProductHierarchy): OptionModel {
        const option: OptionModel = {
            value: parent.ProductTypeId,
            text: parent.LangText,
            title: parent.Number
        };

        const subItems = types.filter(x => {
            const isChild = x.Number !== parent.Number && x.Number.startsWith(parent.Number);
            // get direct child by number
            const isDirectChild = x.Number.length === parent.Number.length + 5;
            return isChild && isDirectChild;
        });

        if (!subItems.length) {
            return option;
        }

        option.children = subItems.map(subItem => {
            return this.getTypeOptionGroupsRecursive(types, subItem);
        });

        return option;
    }

    GetAllBaseColors(query: {
        langCode: string,
        countryId: number
    }, cancelToken: AbortSignal = null) {
        return this.baseApiFactory.getData<any[]>('/api/ProductBaseColor', query, cancelToken);
    };

    GetAllColors(query: {
        langCode: string,
        countryId: number
    }, cancelToken: AbortSignal = null) {
        return this.baseApiFactory.getData<any[]>('/api/ProductColor', query, cancelToken);
    };

    GetAllCerts(query: {
        langCode: string,
        countryId: number
    }, cancelToken: AbortSignal = null) {
        return this.baseApiFactory.getData<Pictogram[]>('/api/Certification', query, cancelToken);
    };

    GetStatus(type, cancelToken: AbortSignal = null) {
        return this.baseApiFactory.getData<{
            IsRunningOrQueued: boolean
        }>(`/api/ProductFeeds/GetStatus/${type}`, null, cancelToken)
    };

    CreateFeeds(color, storeView, langCode) {
        return this.baseApiFactory.post('/api/ProductFeeds/Create', {
            storeView,
            langCode,
            color
        })
    };

    CreateSiteMap() {
        return this.baseApiFactory.post('/api/ProductFeeds/CreateSiteMap', {})
    };

    SyncImagesNewest(number, cancelToken: AbortSignal = null) {
        return this.baseApiFactory.getData<any[]>(`/v2/api/product/SyncImagesNewest/${number}`, null, cancelToken)
    };

    GetCategoriesHavingProduct(countryId, langCode, sku, cancelToken: AbortSignal = null) {
        return this.baseApiFactory.getData<any[]>('/api/Category/GetCategoriesHavingProduct', {
            countryId,
            langCode,
            sku
        }, cancelToken);
    };


    RemoveDataListContent(ids, cancelToken: AbortSignal = null) {
        return this.baseApiFactory.post('/api/Category/RemoveDataListContent', ids, cancelToken);
    };

    GetProductBySkus(langCode, skuString, countryId, cancelToken: AbortSignal = null) {
        return this.baseApiFactory.getData<any[]>('/api/Product/GetProductBySkus', {
            langCode,
            skuString,
            countryId
        }, cancelToken);
    };

    GetHierarchyByGroup(groupId, langCode) {
        return this.baseApiFactory.get('/api/Product/GeProductTypeByGroup?' + "groupId=" + groupId + "&langCode=" + langCode);
    };

    GetProductInfoByCategory(categoryId, langCode, cancelToken: AbortSignal = null) {
        return this.baseApiFactory.getData<ProductInfoByCategory>('/api/Product/GetProductInfoByCategory', {
            categoryId,
            langCode
        }, cancelToken);
    };

    GetProductInfoByGroup(groupId, countryId, langCode, cancelToken: AbortSignal = null) {
        return this.baseApiFactory.getData<GetProductInfoByGroup>('/api/Product/GetProductInfoByGroup', {
            groupId,
            countryId,
            langCode
        }, cancelToken);
    };

    ExportProduct(countryCode: string, langCode: string, cancelToken: AbortSignal = null) {
        this.baseApiFactory.download('/api/Product/ExportToExcel?storeview=' + countryCode + '&lang=' + langCode, {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            fileName: `DataGoogleFB_${countryCode}_${langCode}_` + +new Date().toISOString().split('T')[0]
                .replaceAll('-', '') + ".xlsx"
        }, cancelToken);
    };

}

lazyRegister.service('ProductService', ProductService);
